import { motion } from "framer-motion";
import { Briefcase, HeartHandshake } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const BUSINESS_IMG =
  "https://images.unsplash.com/photo-1472803828399-39d4ac53c6e5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=68&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODM5ODU5NjZ8MA&ixlib=rb-4.1.0";
const LONELINESS_IMG =
  "https://images.unsplash.com/photo-1769344603152-dbfa7bcfcfc4?crop=entropy&cs=srgb&fm=jpg&w=1000&q=68&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxzb2xpdGFyeSUyMGZpZ3VyZSUyMHZhc3QlMjBsYW5kc2NhcGUlMjBkYXJrJTIwY2luZW1hdGljfGVufDB8fHx8MTc4Mzk4NTk2Nnww&ixlib=rb-4.1.0";

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function DualApproach() {
  return (
    <section
      id="approccio"
      className="relative bg-background py-28 md:py-40 border-t border-white/10"
      data-testid="dual-approach-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="mb-16 md:mb-24 max-w-3xl">
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px w-10 bg-primary" />
            <span className="text-[11px] tracking-[0.35em] text-primary">
              AREE DI INTERVENTO
            </span>
          </div>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-foreground leading-tight">
            Lavoro dove il successo e la solitudine si incontrano.
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          {/* Business */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-7 group relative overflow-hidden rounded-2xl border border-white/10 bg-card p-8 md:p-12 min-h-[420px] flex flex-col justify-between"
            data-testid="approach-business"
          >
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={BUSINESS_IMG}
                alt="Business coaching"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover opacity-20 group-hover:opacity-30 group-hover:scale-105 transition-[opacity,transform] duration-[1200ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-transparent" />
            </div>
            <div className="relative z-10">
              <Briefcase className="text-primary" size={28} strokeWidth={1.2} />
            </div>
            <div className="relative z-10">
              <span className="text-[11px] tracking-[0.3em] text-primary">
                BUSINESS COACHING
              </span>
              <h3 className="font-serif text-3xl md:text-4xl text-foreground mt-4 mb-4 leading-tight">
                Trasformare la pressione in direzione.
              </h3>
              <p className="text-muted-foreground max-w-md leading-relaxed">
                Per founder, imprenditori e dirigenti. Lavoriamo su leadership,
                decisioni difficili e performance sostenibile — senza bruciare
                la persona che c'è dietro i risultati.
              </p>
            </div>
          </motion.div>

          {/* Loneliness */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-5 group relative overflow-hidden rounded-2xl border border-white/10 bg-card p-8 md:p-12 min-h-[420px] flex flex-col justify-between"
            data-testid="approach-loneliness"
          >
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={LONELINESS_IMG}
                alt="Loneliness coaching"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover opacity-25 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-[1200ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
            </div>
            <div className="relative z-10">
              <HeartHandshake className="text-primary" size={28} strokeWidth={1.2} />
            </div>
            <div className="relative z-10">
              <span className="text-[11px] tracking-[0.3em] text-primary">
                LONELINESS COACHING
              </span>
              <h3 className="font-serif text-3xl md:text-4xl text-foreground mt-4 mb-4 leading-tight">
                Ritrovare presenza e connessione.
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Per chi ha successo ma si sente solo. Un percorso riservato per
                ricostruire relazioni autentiche — con gli altri e con sé stessi.
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 rounded-2xl border border-white/10 bg-card p-8 md:p-10"
        >
          <p className="font-serif text-2xl md:text-3xl text-foreground max-w-2xl leading-snug">
            Non sai in quale fronte ti riconosci? Spesso sono lo stesso.
          </p>
          <button
            onClick={() => scrollToId("candidatura")}
            className="shrink-0 inline-flex items-center gap-3 rounded-full border border-primary/60 px-8 py-4 text-sm tracking-[0.12em] text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
            data-testid="dual-cta"
          >
            PARLIAMONE →
          </button>
        </motion.div>
      </div>
    </section>
  );
}
